/** @format */

import { i18nContext } from '@/context/i18nContext';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { ProjectCard } from '@/types/shared';
import { Document } from '@contentful/rich-text-types';
import { Entry } from 'contentful';
import { motion } from 'framer-motion';
import { SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { ContainerWrapper } from '../ui/Layout';
import { RichText } from '../ui/RichText';
import { NavigationTitleLink } from '../ui/Typography';
import { Project } from './Project';

export const ProjectsSection = ({ title, projects }: Props) => {
  const theme = useContext(ThemeContext);
  const t = useContext(i18nContext);
  const [cities, setCities] = useState<string[]>([]);
  const [activeCity, setActiveCity] = useState<string>('');
  const [activeCityIndex, setActiveCityIndex] = useState<number>(0);
  const [projectsByCity, setProjectsByCity] = useState<{ [city: string]: ProjectCard[] }>({});
  const [containerWidth, setContainerWidth] = useState<number | undefined>(0);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(0);
  const citiesListRef = useRef<HTMLDivElement>(null);
  const activeCityRef = useRef<(HTMLDivElement | null)[]>([]);
  const cityListRef = useRef<(HTMLLIElement | null)[]>([]);
  const windowWidth = useWindowWidth();

  useEffect(() => {
    const uniqueCities: Set<string> = new Set();

    projects.forEach(obj => {
      uniqueCities.add(obj.fields.city);
    });

    const cityMap: { [city: string]: ProjectCard[] } = projects.reduce((acc, project) => {
      const { city } = project.fields;
      acc[city] = [...(acc[city] || []), project.fields];
      return acc;
    }, {} as Record<string, ProjectCard[]>);

    const uniqueCitiesArray = Array.from(uniqueCities);

    setCities(uniqueCitiesArray);
    setActiveCity(uniqueCitiesArray[0]);
    setProjectsByCity(cityMap);
  }, [projects]);

  const handleSetActiveTab = (city: SetStateAction<string>, index: number, lengthArray: number) => {
    cityListRef.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: index < lengthArray - 2 ? 'center' : 'nearest',
    });
    setActiveCity(city);
    setActiveCityIndex(index);
  };

  useEffect(() => {
    if (!windowWidth) return;

    if (activeCityRef && activeCityRef.current && activeCityIndex && activeCityRef.current[activeCityIndex]) {
      setContainerWidth(activeCityRef.current[activeCityIndex]?.clientWidth);
      setContainerHeight(activeCityRef.current[activeCityIndex]?.clientHeight);
    }
  }, [windowWidth]);

  useEffect(() => {
    if (activeCityRef && activeCityRef.current && activeCityIndex && activeCityRef.current[activeCityIndex]) {
      setContainerWidth(activeCityRef.current[activeCityIndex]?.clientWidth);
      setContainerHeight(activeCityRef.current[activeCityIndex]?.clientHeight);
    }
  }, [activeCityRef]);

  const horizontalInitPosition = (activeCityRef.current[activeCityIndex]?.clientWidth! * activeCityIndex) | 0;
  const projectWrapperHeight = activeCityRef.current[activeCityIndex]?.clientHeight;

  return (
    <ContainerWrapperCustom $color={theme!.color.white}>
      <TitleWrapper>{title && <RichText text={title} />}</TitleWrapper>
      <CitiesTabWrapper>
        <CitiesContainer ref={citiesListRef}>
          <CitiesList>
            {cities.map((city, index) => (
              <CityList key={city} ref={ref => (cityListRef.current[index] = ref)}>
                <CityTabButton
                  className={activeCity === city ? 'active' : ''}
                  onClick={e => handleSetActiveTab(city, index, cities.length)}
                >
                  <NavigationTitleLink>{t.common[city.toLowerCase()]}</NavigationTitleLink>
                </CityTabButton>
              </CityList>
            ))}
          </CitiesList>
        </CitiesContainer>
      </CitiesTabWrapper>
      <ProjectsContainer>
        <ProjectsWrapper
          $citiesNumer={cities.length}
          initial={false}
          animate={{
            x: `-${horizontalInitPosition}px`,
            height: `${projectWrapperHeight}px`,
          }}
          transition={{ type: 'spring', bounce: 0.12 }}
        >
          {Object.keys(projectsByCity).map((cityName, index) => (
            <ProjectsByCityWrapper
              $citiesNumer={cities.length}
              ref={ref => (activeCityRef.current[index] = ref)}
              key={cityName}
              className={cityName === activeCity ? 'active' : ''}
            >
              {projectsByCity[cityName].map((project, index) => (
                <Project key={project.title + index} project={project} />
              ))}
            </ProjectsByCityWrapper>
          ))}
        </ProjectsWrapper>
      </ProjectsContainer>
    </ContainerWrapperCustom>
  );
};

const ContainerWrapperCustom = styled(ContainerWrapper)`
  overscroll-behavior-x: none;
`;

const TitleWrapper = styled.div`
  text-align: center;
  padding-block: clamp(2rem, 1.2933rem + 3.1407vw, 4.12rem) clamp(2rem, 1.2933rem + 3.1407vw, 4.62rem);
  h2 {
    color: ${({ theme }) => theme.color.teal.dark};
  }
`;

const CitiesTabWrapper = styled.div`
  border-block: 1px solid ${({ theme }) => theme.color.teal.light};
`;

const CitiesContainer = styled.div`
  max-width: 1440px;
  margin: auto;
  height: 105px;
`;
const CitiesList = styled.ul`
  display: flex;
  flex-direction: row;
  width: 100%;
  overflow-x: auto;
  gap: 0 clamp(0.94rem, 0.2733rem + 2.963vw, 2.94rem);
  list-style: none;
  padding-block: 1.62rem;
  padding-bottom: 3rem;
`;

const CityList = styled.li`
  --padding: ${({ theme }) => theme.container.smallPadding};
  &:last-child {
    padding-right: var(--padding);
  }

  &:first-child {
    @media (max-width: 1520px) {
      padding-left: var(--padding);
    }
  }
`;
const CityTabButton = styled.button`
  cursor: pointer;
  padding: 0.94rem 1.81rem;
  text-transform: uppercase;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.teal.dark};
  transition: background-color 0.3s, border 0.3s;
  border-radius: 0.625rem;

  &.active {
    background-color: ${({ theme }) => theme.color.yellow};
    border: 1px solid ${({ theme }) => theme.color.white};
    transition: background-color 0.3s, border 0.3s;
  }
`;

const ProjectsContainer = styled.div`
  overflow: hidden;
`;

const ProjectsWrapper = styled(motion.div)<{ $citiesNumer?: number }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  width: ${({ $citiesNumer }) => `calc(100% * ${$citiesNumer})`};
  overflow-y: hidden;
  background-color: #fff;
  will-change: transform, height;
`;

const ProjectsByCityWrapper = styled.div<{ $citiesNumer?: number }>`
  width: ${({ $citiesNumer }) => `calc(100% / ${$citiesNumer})`};
  padding-block: clamp(1.88rem, 0.9667rem + 4.0593vw, 4.62rem) 0;
`;

type Props = {
  title?: Document;
  projects: Entry<ProjectCard>[];
};
